import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import * as Sentry from '@sentry/vue'

import { api } from '@/services'
import { useApiStatus, statusOptions } from '@/composables/apiStatus'

const useCompanyGroupsStore = defineStore('companyGroups', () => {
  const currentCachedCompany = ref()
  const currentCachedCountsCompany = ref()

  const { status, isIdle, isFetching, hasError, isSuccess } = useApiStatus()

  const groups = ref([])
  const groupUserCounts = ref({})

  const groupsById = computed(() =>
    Object.fromEntries(groups.value.map((group) => [group.id, group]))
  )

  const fetchCompanyGroups = async (companyId) => {
    if (currentCachedCompany.value === companyId) return
    const companyGroupsEndpoint = `/core-services/company-groups?companyId=${companyId}`
    groups.value = []
    status.value = statusOptions.fetching

    try {
      const response = await api.get(companyGroupsEndpoint)
      groups.value = response.data.data
      status.value = statusOptions.success
      currentCachedCompany.value = companyId
    } catch (error) {
      status.value = statusOptions.error
    }
  }

  const fetchGroupUserCounts = async (companyId) => {
    if (currentCachedCountsCompany.value === companyId) return
    if (groups.value.length === 0) return

    const groupIds = groups.value.map((group) => group.id).join(',')
    const countsEndpoint = `/core-services/company-users/companies/${companyId}/users/count?groups=${groupIds}`

    try {
      const response = await api.get(countsEndpoint)
      groupUserCounts.value = response.data.data
      currentCachedCountsCompany.value = companyId
    } catch (error) {
      groupUserCounts.value = {}
      console.error(error)
      Sentry.captureException(error)
    }
  }

  return {
    groups,
    groupsById,
    groupUserCounts,
    status,
    isIdle,
    isFetching,
    hasError,
    isSuccess,
    fetchCompanyGroups,
    fetchGroupUserCounts
  }
})

export default useCompanyGroupsStore
